/**
 * Viewport culling utilities (PIPH-72).
 *
 * Pure functions that decide whether an atom or bond intersects a given
 * viewport rectangle, used by `AtomLayer` and `BondLayer` to skip rendering
 * elements that would be entirely off-screen.
 *
 * All coordinates are in the same space (SVG y-down) as the atoms' render
 * data. The viewport is specified as a rectangle in SVG coordinates — the
 * caller (typically a `ViewportCullingProvider` fed by `useZoomPan`) is
 * responsible for keeping these coordinates synchronized with what's
 * actually being shown on screen.
 *
 * ## Why a margin is required
 *
 * Atoms near the edge of the viewport have a visual extent (text glyph,
 * paper-carbon circle) that can extend past the atom's center point. An
 * atom whose center is just outside the viewport is still partially
 * on-screen and must be rendered.
 *
 * Bonds connect two atoms. A bond is visible if either endpoint is
 * on-screen OR if the bond's line segment crosses the viewport. For typical
 * chemistry (bond length ~1 Å, viewport ~50 Å at standard zoom) the
 * line-segment case is covered by expanding the endpoint check by a margin
 * equal to the bond length.
 *
 * The default `DEFAULT_CULLING_MARGIN` (6.5 Å, the Alchemie standard bond
 * length) is conservative enough for both atom radius and bond extent.
 *
 * @package @alchemiesolns/svg-interactive
 */

import type { AtomRenderData } from './useSVGGeneration';
import type { BondRecipe } from './types/bondRecipe.types';

/**
 * Axis-aligned bounding rectangle in SVG coordinates (y-down).
 *
 * Typically derived from a `useZoomPan` viewBox: `{ x, y, width, height }`
 * maps to `{ minX: x, minY: y, maxX: x + width, maxY: y + height }`.
 */
export interface ViewportBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Default culling margin in SVG units (Ångströms).
 *
 * Matches Alchemie's standard bond length (6.5 Å). A margin of one bond
 * length is enough to cover:
 *   - atom visual extent (text glyphs, paper-carbon circles)
 *   - bonds that cross the viewport edge from an atom just outside
 *
 * For workspaces with unusually long bonds or large atom labels, callers
 * can override this via the `margin` parameter.
 */
export const DEFAULT_CULLING_MARGIN = 6.5;

/**
 * Returns true if an atom's position (with margin) intersects the viewport.
 *
 * The margin is applied symmetrically — an atom is considered visible if
 * its center is within `margin` units of the viewport rectangle on any
 * side. This handles atom visual extent without requiring per-atom size
 * information.
 */
export function isAtomInViewport(
  atom: AtomRenderData,
  bounds: ViewportBounds,
  margin: number = DEFAULT_CULLING_MARGIN,
): boolean {
  return (
    atom.x >= bounds.minX - margin &&
    atom.x <= bounds.maxX + margin &&
    atom.y >= bounds.minY - margin &&
    atom.y <= bounds.maxY + margin
  );
}

/**
 * Returns true if a bond is potentially visible in the viewport.
 *
 * A bond is visible if either endpoint is within `margin` of the viewport
 * rectangle. This covers the common cases (both endpoints visible, one
 * endpoint visible) and the edge case of a bond whose endpoints are both
 * outside the viewport but whose segment crosses it — because the margin
 * is one bond length, an atom that's one bond away from the viewport
 * (meaning its bond *does* cross the viewport) is treated as visible.
 *
 * Returns true (no culling) if either endpoint can't be resolved from the
 * `atomsById` map — preferring over-rendering to silent data loss.
 */
export function isBondInViewport(
  recipe: BondRecipe,
  atomsById: ReadonlyMap<string, AtomRenderData>,
  bounds: ViewportBounds,
  margin: number = DEFAULT_CULLING_MARGIN,
): boolean {
  const atom1 = atomsById.get(recipe.atom1Id);
  const atom2 = atomsById.get(recipe.atom2Id);
  if (!atom1 || !atom2) return true;

  return (
    isAtomInViewport(atom1, bounds, margin) ||
    isAtomInViewport(atom2, bounds, margin)
  );
}

/**
 * Convert a `useZoomPan`-style viewBox object into `ViewportBounds`.
 *
 * @example
 *   const bounds = boundsFromViewBox({ x: -20, y: -15, width: 60, height: 40 });
 *   // { minX: -20, minY: -15, maxX: 40, maxY: 25 }
 */
export function boundsFromViewBox(viewBox: {
  x: number;
  y: number;
  width: number;
  height: number;
}): ViewportBounds {
  return {
    minX: viewBox.x,
    minY: viewBox.y,
    maxX: viewBox.x + viewBox.width,
    maxY: viewBox.y + viewBox.height,
  };
}
